import { useState, useMemo } from "react";
import { useDates } from "dates/DatesContext";
import moment from "moment";
import { useAppSelector } from "../../../../redux/hooks";
import { Row, Col, Nav } from "react-bootstrap";
import TrellisNavLink from "components/core/basic/NavLink";
import { ButtonGroup, type ButtonDefinition } from "../../../../components/core/basic/ButtonGroup";
import { Center, Flex } from "../../../../../styled-system/jsx";
import { useFetch } from "../../../../hooks/api";
import PricingPlanTable, {
  type DashboardStats,
  type PlanStrategyPlanList,
  type ScatterStats,
  type Limits,
} from "../../PricingPlanTable";
import { keepPreviousData } from "@tanstack/react-query";
import { MetricCards } from "./MetricCards";
import PriceLog from "modules/pricingv2/components/PriceLog";
import { ExplainerBox } from "components/core/basic/ExplainerBox";
import { CategorySelectorModal } from "../../../categories/CategorySelector";
import { TagSelectorModal } from "../../../tags/TagSelector";
import { useCategoriesQuery } from "../../../../hooks/useCategories";
import { usePricingOrg, type PricingOrg } from "../../../../hooks/usePricingOrg";
import { Tooltip, TooltipTrigger } from "react-aria-components";
import { useModal } from "@ebay/nice-modal-react";
import { Button } from "../../../../components/core/basic/Button";
import { css } from "../../../../../styled-system/css";
import { cut } from "utils/formatText";
import {
  type InventoryStats,
  type PlanData,
  type GroupedStats,
  type InventoryBuckets,
} from "modules/pricingv2/pricingTypes";
import EvolutionTab from "./V4Evolution";

type Buckets = "all" | "volume" | "balanced" | "margin" | "override_supply"; // | "constrained";

const GetScatterData = (op: DashboardStats[]): ScatterStats => {
  const scatterData: { x: number; y: number; name: string; planId: number }[] = [];
  let minX = 0;
  let maxX = 0;
  let minY = 0;
  let maxY = 0;

  op.forEach((plan) => {
    const x = plan.inc_contribution;
    const y = plan.inc_units;
    const planName = cut(plan.plan_name || "", 50);
    const planId = parseInt(plan.name) || 0;
    scatterData.push({ x, y, name: planName, planId: planId });

    if (x < minX) {
      minX = x;
    }
    if (x > maxX) {
      maxX = x;
    }
    if (y < minY) {
      minY = y;
    }
    if (y > maxY) {
      maxY = y;
    }
  });

  if (Math.abs(minX) > Math.abs(maxX)) {
    maxX = Math.abs(minX);
  } else {
    minX = -Math.abs(maxX);
  }
  if (Math.abs(minY) > Math.abs(maxY)) {
    maxY = Math.abs(minY);
  } else {
    minY = -Math.abs(maxY);
  }

  minY -= 10;
  maxY += 10;
  minX -= 10;
  maxX += 10;

  return {
    scatterData,
    minX,
    maxX,
    minY,
    maxY,
  };
};

export function V4Metrics() {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const { group, start, end, preStart, preEnd } = useDates();
  const today = moment().format("YYYY-MM-DD");
  const endIsToday = end.format("YYYY-MM-DD") === today;
  const bucketPreStart = endIsToday ? preStart.clone().add(1, "days") : preStart;
  const [tab, setTab] = useState("plans");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedBucket, setSelectedBucket] = useState<Buckets>("all");
  const [selectedPlanStrategy, setSelectedPlanStrategy] = useState<string>("all");
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [XYLimit, setXYLimit] = useState<Limits | null>(null);
  const [healthSelection, setHealthSelection] = useState<string | undefined>();
  const categorySelectorModal = useModal(CategorySelectorModal);
  const tagSelectorModal = useModal(TagSelectorModal);
  const { pricingOrg, isPricingOrgLoading } = usePricingOrg() as {
    pricingOrg: PricingOrg;
    isPricingOrgLoading: boolean;
  };

  const filters = {
    mp: marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: bucketPreStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    empty: true,
    group_by: "date",
    // tags: selectedTags.length > 0 ? selectedTags.join(",") : null,
  };

  const dashboardData = useFetch<{
    total: DashboardStats;
    date: DashboardStats[];
    plan_id: DashboardStats[];
    plan_strategies: PlanStrategyPlanList;
  }>(["pricingplan_report_v4", marketPlace, start, end, preStart, preEnd], `/pricing/dashboard_v4_stats`, filters, {
    select: (d) => d.data,
    placeholderData: keepPreviousData,
  });

  const planStrategyData = useFetch<{
    plan_strategies: PlanStrategyPlanList;
  }>(
    ["pricingplan_strategy_v4", marketPlace],
    `/pricing/dashboard_v4_stats`,
    {
      strategies_only: true,
      mp: marketPlace,
      category_ids: selectedCategories.length > 0 ? selectedCategories.join(",") : null,
      tags: selectedTags.length > 0 ? selectedTags.join(",") : null,
    },
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const inventoryAwareData = useFetch<{ metrics: InventoryStats[]; plan_id: PlanData[] }>(
    ["inventory_dashboard", marketPlace, start, end],
    `/pricing/inventory_dashboard`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
      enabled: true,
    },
  );

  let volumeCount = 0;
  let balancedCount = 0;
  let marginCount = 0;
  let overrideSupply = 0;

  if (planStrategyData.isSuccess) {
    volumeCount = planStrategyData.data.plan_strategies.volume.length;
    balancedCount = planStrategyData.data.plan_strategies.balanced.length;
    marginCount = planStrategyData.data.plan_strategies.margin.length;
    overrideSupply = planStrategyData.data.plan_strategies.override_supply.length;
  }

  const totalCount = volumeCount + balancedCount + marginCount;

  const buttunBuckets: ButtonDefinition<Buckets>[] = [
    { label: "All Plans", value: "all", counter: totalCount },
    { label: "Volume", value: "volume", counter: volumeCount },
    {
      label: "Balanced",
      value: "balanced",
      counter: balancedCount,
    },
    { label: "Margin", value: "margin", counter: marginCount },
  ];

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!isPricingOrgLoading && pricingOrg && pricingOrg.supply_target_program) {
    buttunBuckets.push({
      label: "Inventory Aware",
      value: "override_supply",
      counter: overrideSupply,
    });
  }

  const InventoryEvolutionTab = () => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!isPricingOrgLoading && pricingOrg && pricingOrg.supply_target_program) {
      return (
        <TrellisNavLink eventKey="evolution" className="text-start">
          Inventory Evolution
        </TrellisNavLink>
      );
    }

    return null;
  };

  const {
    categoryCount,
    filteredPricingPlans,
    scatterData,
    tags,
    tagCount,
    pricingPlans,
  }: {
    categoryCount: Record<number, number>;
    filteredPricingPlans: {
      total: DashboardStats;
      date: DashboardStats[];
      plan_id: DashboardStats[];
      plan_strategies: PlanStrategyPlanList;
    };
    scatterData: ScatterStats;
    tags: string[];
    tagCount: Record<string, number>;
    pricingPlans: DashboardStats[];
  } = useMemo(() => {
    function getEmptyDashboardStats(): DashboardStats {
      return {
        name: "",
        inc_cashflow: 0,
        inc_contribution: 0,
        inc_price: 0,
        inc_units: 0,
        price_decreased: 0,
        price_decreased_avg: 0,
        price_increased: 0,
        price_increased_avg: 0,
        reference_cr: 0,
        reference_price: 0,
        target_sales: 0,
        target_units: 0,
        total_sales: 0,
        total_units: 0,
        page_views: 0,
        value: 0,
        asp: 0,
      };
    }

    const tagCount: Record<string, number> = {};
    const pricingPlans: DashboardStats[] = [];

    if (!dashboardData.isSuccess) {
      return {
        categoryCount: {},
        filteredPricingPlans: {
          total: getEmptyDashboardStats(),
          date: [],
          plan_id: [],
          plan_strategies: { volume: [], balanced: [], margin: [], constrained: [], override_supply: [] },
        },
        scatterData: {
          scatterData: [],
          minX: 0,
          maxX: 0,
          minY: 0,
          maxY: 0,
        },
        tags: [],
        tagCount: tagCount,
        pricingPlans: [],
      };
    }

    const op = dashboardData.data.plan_id;

    const categoryCount: Record<number, number> = {};
    const total: DashboardStats = getEmptyDashboardStats();
    const date: DashboardStats[] = [];
    const tags: string[] = [];

    const healthPlans: Record<string, number[]> = {};

    inventoryAwareData.data?.plan_id.forEach((plan) => {
      if (healthPlans[plan.health] === undefined) {
        healthPlans[plan.health] = [];
      }
      if (healthPlans[plan.health]) {
        healthPlans[plan.health]!.push(plan.plan_id);
      }
    });

    op.forEach((plan) => {
      if (plan.category_id !== undefined) {
        if (categoryCount[plan.category_id]) {
          categoryCount[plan.category_id] = (categoryCount[plan.category_id] ?? 0) + 1;
        } else {
          categoryCount[plan.category_id] = 1;
        }
      }

      if (plan.tags) {
        plan.tags.forEach((tag) => {
          if (!tags.includes(tag)) {
            tags.push(tag);
            tagCount[tag] = 1;
          } else {
            tagCount[tag] = (tagCount[tag] ?? 0) + 1;
          }
        });
      }

      let addPlan = false;
      if (selectedPlanStrategy === "volume") {
        if (plan.configured_slider_value !== undefined && plan.configured_slider_value < 0) {
          addPlan = true;
        }
      } else if (selectedPlanStrategy === "balanced") {
        if (plan.configured_slider_value !== undefined && plan.configured_slider_value === 0) {
          addPlan = true;
        }
      } else if (selectedPlanStrategy === "margin") {
        if (plan.configured_slider_value !== undefined && plan.configured_slider_value > 0) {
          addPlan = true;
        }
      } else if (selectedPlanStrategy === "override_supply") {
        if (plan.override_supply) {
          addPlan = true;
        }
      } else {
        addPlan = true;
      }
      if (addPlan && selectedCategories.length > 0) {
        if (plan.category_id !== undefined && selectedCategories.includes(plan.category_id)) {
          addPlan = true;
        } else {
          addPlan = false;
        }
      }

      if (addPlan && healthSelection !== undefined) {
        if (
          healthPlans[healthSelection] &&
          plan.plan_id !== undefined &&
          healthPlans[healthSelection].includes(plan.plan_id)
        ) {
          addPlan = true;
        } else {
          addPlan = false;
        }
      }

      if (addPlan && XYLimit) {
        const x = plan.inc_contribution;
        const y = plan.inc_units;

        if (x < XYLimit.incContributionMin || x > XYLimit.incContributionMax) {
          addPlan = false;
        }
        if (y < XYLimit.incUnitsMin || y > XYLimit.incUnitsMax) {
          addPlan = false;
        }
      }

      if (addPlan && selectedTags.length > 0) {
        if (plan.tags) {
          const tags = plan.tags.map((tag) => tag.toLowerCase());
          const selectedTagsLower = selectedTags.map((tag) => tag.toLowerCase());
          const hasTag = selectedTagsLower.some((tag) => tags.includes(tag));
          if (hasTag) {
            addPlan = true;
          } else {
            addPlan = false;
          }
        } else {
          addPlan = false;
        }
      }

      if (addPlan) {
        total.inc_cashflow += plan.inc_cashflow;
        total.inc_contribution += plan.inc_contribution;
        total.inc_price += plan.inc_price;
        total.inc_units += plan.inc_units;
        total.price_decreased += plan.price_decreased;
        total.price_decreased_avg += plan.price_decreased_avg;
        total.price_increased += plan.price_increased;
        total.price_increased_avg += plan.price_increased_avg;
        total.reference_cr += plan.reference_cr;
        total.reference_price += plan.reference_price;
        total.target_sales += plan.target_sales;
        total.target_units += plan.target_units;
        total.total_sales += plan.total_sales;
        total.total_units += plan.total_units;
        total.page_views += plan.page_views;
        total.value += plan.value;
        total.name = "Total";

        total.asp = total.total_units > 0 ? total.total_sales / total.total_units : 0;
        total.reference_cr = total.page_views > 0 ? total.total_units / total.page_views : 0;
        pricingPlans.push(plan);
      }
    });

    const scatterStats: ScatterStats = GetScatterData(pricingPlans);

    return {
      categoryCount,
      filteredPricingPlans: {
        total: total,
        date: date,
        plan_id: pricingPlans,
        plan_strategies: dashboardData.data.plan_strategies,
      },
      scatterData: scatterStats,
      tags: tags,
      tagCount: tagCount,
      pricingPlans: pricingPlans,
    };
  }, [
    dashboardData,
    selectedPlanStrategy,
    selectedCategories,
    XYLimit,
    selectedTags,
    inventoryAwareData,
    healthSelection,
  ]);

  const {
    inventoryMetrics,
  }: {
    inventoryMetrics: GroupedStats;
  } = useMemo(() => {
    if (!inventoryAwareData.data) {
      const inventoryMetrics: GroupedStats = {};
      const categoryCount: Record<number, number> = {};
      return {
        inventoryMetrics: inventoryMetrics,
        categoryCount: categoryCount,
      };
    }
    const inventoryMetrics: GroupedStats = inventoryAwareData.data.metrics.reduce(
      (acc: GroupedStats, item: InventoryStats) => {
        const { report_date, health, count, forecast_units, total_units, forecast_revenue, total_sales, cost } = item;

        if (!acc[report_date]) {
          acc[report_date] = {
            health: {} as Record<
              InventoryBuckets,
              {
                count: number;
                forecast_units: number;
                total_units: number;
                forecast_revenue: number;
                total_sales: number;
                cost: number;
              }
            >,
          };
        }

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!acc[report_date].health[health]) {
          acc[report_date].health[health] = {
            count: 0,
            forecast_units: 0,
            total_units: 0,
            forecast_revenue: 0,
            total_sales: 0,
            cost: 0,
          };
        }

        acc[report_date].health[health].count += count;
        acc[report_date].health[health].forecast_units += forecast_units;
        acc[report_date].health[health].total_units += total_units;
        acc[report_date].health[health].forecast_revenue += forecast_revenue;
        acc[report_date].health[health].total_sales += total_sales;
        acc[report_date].health[health].cost += cost;

        return acc;
      },
      {},
    );

    return { inventoryMetrics };
  }, [inventoryAwareData]);

  return (
    <>
      <Flex gap="small" justifyContent={"space-between"} alignContent={"center"} paddingBottom={"medium"} wrap="wrap">
        {!planStrategyData.isLoading && (
          <ButtonGroup
            buttons={buttunBuckets}
            value={selectedBucket}
            onSelect={(values) => {
              setSelectedPlanStrategy(values);
              setSelectedBucket(values);
            }}
          />
        )}

        <div className="d-flex flex-column pt-3">
          {dashboardData.isFetching && <span className="fs-4 ps-3">Loading Plan Strategies...</span>}
        </div>

        <Flex gap="small" justifyContent={"space-between"} alignContent={"center"} paddingBottom={"medium"} wrap="wrap">
          <TagSelectorButton
            onClick={() => {
              void tagSelectorModal.show({
                initialSelectedTags: selectedTags,
                onConfirm: (value) => {
                  setSelectedTags(value);
                },
                getIrrelevantTagsHiddenText: (num) => `${num} tags without plans not shown`,
                sortTags: (a, b) => {
                  const aCnt = tagCount[a] ?? 0;
                  const bCnt = tagCount[b];
                  if (aCnt && bCnt) {
                    return bCnt - aCnt;
                  } else if (aCnt) {
                    return -1;
                  } else if (bCnt) {
                    return 1;
                  } else {
                    return 0;
                  }
                },
                hideIrrelevantTags: (tag) => {
                  if (tagCount[tag]) {
                    return false;
                  }
                  return true;
                },
                tags: tags,
                tagCount: tagCount,
              });
            }}
            seleectedTags={selectedTags}
            tags={tags}
          />

          <CategorySelectorButton
            onClick={() => {
              void categorySelectorModal.show({
                initialSelectedCategories: selectedCategories,
                onConfirm: (value) => {
                  setSelectedCategories(value);
                },

                getIrrelevantCategoriesHiddenText: (num) => `${num} categories without plans not shown`,
                sortCategories: (a, b) => {
                  const aCnt = categoryCount[a.id] ?? 0;
                  const bCnt = categoryCount[b.id];
                  if (aCnt && bCnt) {
                    return bCnt - aCnt;
                  } else if (aCnt) {
                    return -1;
                  } else if (bCnt) {
                    return 1;
                  } else {
                    return 0;
                  }
                },
                hideIrrelevantCategories: (category) => {
                  if (categoryCount[category.id]) {
                    return false;
                  }
                  return true;
                },
                getDescription: (category) => {
                  if (categoryCount[category.id]) {
                    return `Number of plans: ${categoryCount[category.id]}`;
                  }
                  return "";
                },
              });
            }}
            selectedCategories={selectedCategories}
          />
        </Flex>
      </Flex>

      <Row>
        <Col className="mt-5 ph-4 mb-3 fs-standard" xs={12}>
          <MetricCards
            dashboardData={filteredPricingPlans}
            isLoading={dashboardData.isLoading}
            marketPlace={marketPlace}
            pricingPlan={{ enroll_date: start.format("YYYY-MM-DD") }}
            fetching={dashboardData.isFetching || dashboardData.isLoading}
            singlePlan={false}
            scatterData={scatterData}
            setXYLimit={setXYLimit}
            selectedPlanStrategy={selectedPlanStrategy}
            inventoryMetrics={inventoryMetrics}
            healthSelection={healthSelection}
            setHealthSelection={setHealthSelection}
          />
        </Col>

        <Col className="mt-5 ph-4 mb-3 border-bottom fs-standard" xs={12}>
          <Nav
            variant="underline"
            onSelect={(k) => {
              if (k !== null) {
                setTab(k);
              }
            }}
            activeKey={tab}
          >
            <TrellisNavLink eventKey="plans" className="text-start">
              Pricing Plans
            </TrellisNavLink>
            <TrellisNavLink eventKey="price_changes" className="text-start">
              Price Changes
            </TrellisNavLink>
            <InventoryEvolutionTab />
          </Nav>
        </Col>
      </Row>

      {tab === "plans" && (
        <Row>
          <Col className="py-0">
            {!dashboardData.isLoading && dashboardData.data?.plan_id && <PricingPlans pricingPlans={pricingPlans} />}
          </Col>
        </Row>
      )}
      {tab === "price_changes" && (
        <Row>
          <Col className="py-0">
            <PriceLog tabList={["price_increases", "price_decreases"]} vertical />
          </Col>
        </Row>
      )}

      {tab === "evolution" && (
        <Row>
          <Col className="py-0">
            <EvolutionTab inventoryMetrics={inventoryMetrics} />
          </Col>
        </Row>
      )}

      {tab === "price_mismatches" && (
        <Row>
          <Col className="py-0">
            <ExplainerBox>
              These are Pricing Plans that have been flagged because the price listed on Amazon does not match the price
              set in Dynamic Pricing. Several factors can cause these discrepancies, including:
              <ul>
                <li>Amazon applying automatic discounts or promotions (such as coupons or “Sale Price” overrides).</li>
                <li>
                  Amazon enforcing a “Minimum Price” or “Pricing Error” policy that adjusts listings outside of our
                  intended updates.
                </li>
                <li>Manual adjustments made directly on Amazon Seller Central that override Dynamic Pricing feeds.</li>
              </ul>
            </ExplainerBox>
            <PriceLog tabList={["price_mismatches"]} />
          </Col>
        </Row>
      )}
    </>
  );
}

function CategorySelectorButton({
  onClick,
  selectedCategories,
}: {
  onClick: () => void;
  selectedCategories: number[];
}) {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const categoryQuery = useCategoriesQuery(marketPlace, channel);
  return (
    <TooltipTrigger>
      <Button onPress={onClick} variant={"primary"}>
        {`Select Categories ${selectedCategories.length > 0 ? `(${selectedCategories.length})` : ""}`}
      </Button>
      <Tooltip
        placement={"bottom"}
        className={css({
          border: "1px solid {colors.lightgrey}",
          borderRadius: "light",
          padding: "medium",
          fontSize: "medium",
          backgroundColor: "white",
        })}
        offset={10}
      >
        {selectedCategories.length ?
          <figure>
            <figcaption>Selected Categories:</figcaption>
            <ul>
              {categoryQuery.data
                ?.filter((c) => selectedCategories.includes(c.id))
                .map((category) => <li key={category.id}>{category.leaf_category_name}</li>)}
            </ul>
          </figure>
        : <p
            className={css({
              margin: "0",
              verticalAlign: "middle",
            })}
          >
            Showing all categories
          </p>
        }
      </Tooltip>
    </TooltipTrigger>
  );
}

function TagSelectorButton({
  onClick,
  seleectedTags,
  tags,
}: {
  onClick: () => void;
  seleectedTags: string[];
  tags: string[];
}) {
  return (
    <TooltipTrigger>
      <Button onPress={onClick} variant={"primary"}>
        {`Select Tags ${seleectedTags.length > 0 ? `(${seleectedTags.length})` : ""}`}
      </Button>
      <Tooltip
        placement={"bottom"}
        className={css({
          border: "1px solid {colors.lightgrey}",
          borderRadius: "light",
          padding: "medium",
          fontSize: "medium",
          backgroundColor: "white",
        })}
        offset={10}
      >
        {seleectedTags.length ?
          <figure>
            <figcaption>Selected Tags:</figcaption>
            <ul>
              {tags
                .filter((c) => seleectedTags.includes(c))
                .map((tag) => (
                  <li key={tag}>{tag}</li>
                ))}
            </ul>
          </figure>
        : <p
            className={css({
              margin: "0",
              verticalAlign: "middle",
            })}
          >
            Showing all tags
          </p>
        }
      </Tooltip>
    </TooltipTrigger>
  );
}

function PricingPlans({ pricingPlans }: { pricingPlans: DashboardStats[] }) {
  if (pricingPlans.length === 0) {
    return (
      <Center marginTop="large" color="text.secondary" fontSize={"xxlarge"} textAlign={"center"}>
        No Pricing Plans match your selection
      </Center>
    );
  }

  return <PricingPlanTable pricingPlans={pricingPlans} fromV4 />;
}
